import PropTypes from 'prop-types';
import React, { memo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import Popup from '../Popup';
import Button from '../Button';

import style from './style.module.css';

const POPUP_WIDTH = 900;

/**
 * PDF viewer popup rendered over the current global popup (popups do not stack in the popup context).
 *
 * @param props {Object} Component props.
 * @param props.url {String} PDF URL (e.g. Blob object URL).
 * @param props.header {String} Popup header.
 * @param props.onClose {Function} Close handler.
 * @param props.onDownload {Function} Footer Download button handler.
 * @param [props.loading = false] {Boolean} Shows the loading overlay.
 */
const PdfViewerPopup = ({ url, header, onClose, onDownload, loading = false }) => {
  const hostRef = useRef(null);

  // Native listener: the global Escape handler listens on `document.body` (the portal container), so it must be
  // stopped before the event reaches body, which a React handler cannot guarantee.
  useEffect(() => {
    const host = hostRef.current;

    const handleKeyUp = event => {
      if (event.key !== 'Escape') return;

      event.stopPropagation();
      onClose();
    };

    host.addEventListener('keyup', handleKeyUp);

    return () => host.removeEventListener('keyup', handleKeyUp);
  }, [onClose]);

  const footer = (
    <Button className="btn-primary" disabled={loading} onClick={onDownload} type="button">
      Download
    </Button>
  );

  return createPortal(
    <div ref={hostRef}>
      <div className={classNames('modal-backdrop fade in', style.backdrop)} />
      <Popup
        className={style.host}
        footer={footer}
        header={header}
        id="pdfViewerPopup"
        loading={loading}
        onClose={onClose}
        width={POPUP_WIDTH}
      >
        <iframe className={style.frame} src={url} title={header} />
      </Popup>
    </div>,
    document.body,
  );
};

PdfViewerPopup.propTypes = {
  header: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  url: PropTypes.string.isRequired,
};

export default memo(PdfViewerPopup);
